"use client"

import Link from "next/link"
import { Clock, User } from "lucide-react"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import type { Article } from "@/lib/types"

interface LatestNewsListProps {
  articles: Article[]
}

export function LatestNewsList({ articles }: LatestNewsListProps) {
  const getExcerpt = (content: string) => {
    const text = content.replace(/<[^>]*>/g, "").trim()
    return text.length > 150 ? text.substring(0, 150) + "..." : text
  }

  if (articles.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p>No latest news available</p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {articles.map((article) => (
        <Card 
          key={article.id} 
          style={{
            display: "block",
            position: "relative",
            background: "#fff",
            borderRadius: "5px",
            boxShadow: "1px 1px 5px rgba(0, 0, 0, .12)"
          }}
        >
          <Link href={`/article/${article.slug}`}>
            <CardContent className="p-0">
              <div className="grid md:grid-cols-3 gap-3 p-0">
                {/* Image */}
                <div className="relative h-48 md:h-32 overflow-hidden rounded-none">
                  {article.featured_image ? (
                    <img
                      src={article.featured_image}
                      alt={article.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full bg-muted flex items-center justify-center">
                      <span className="text-muted-foreground text-xs">No Image</span>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="md:col-span-2 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      {article.category && (
                        <Badge variant="secondary" className="text-xs">
                          {article.category.name}
                        </Badge>
                      )}
                    </div>
                    <h3 style={{ display: "block", position: "relative", fontSize: "20px", lineHeight: "32px", fontWeight: 700 }} className="mb-2 text-foreground line-clamp-2 hover:text-primary transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                      {getExcerpt(article.content)}
                    </p>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    {article.author && (
                      <div className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        <span>{article.author.name}</span>
                      </div>
                    )}
                    {article.read_time && (
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        <span>{article.read_time} min</span>
                      </div>
                    )}
                    {article.created_at && (
                      <span>{new Date(article.created_at).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Link>
        </Card>
      ))}
    </div>
  )
}

